import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DataTable, { type TableColumn } from "react-data-table-component";
import { TablePager } from "../shared/TablePager";
import { useToast } from "../toast/toastContext";
import { adminTableTheme } from "../admin/adminTableTheme";
import { EmptyState, ErrorState, LoadingState } from "../states";
import adminStyles from "../admin/Admin.module.css";
import styles from "./ApproverRequests.module.css";
import { useAccessGroupCatalog, type AccessGroupCatalog } from "../allocation/useAccessGroupCatalog";
import { shortEmail } from "./requestHistoryFormat";
import { RequestDetailContent } from "./RequestHistoryTable";
import { DecisionRow } from "./DecisionRow";
import { decisionHeadline } from "./decisionHeadline";
import { isProviderAllocationRequest } from "../../api/requestTypes";
import { completeManualTask, decideRequest, fetchApprovers, fetchPendingRequests, reassignRequest, type Approver, type RequestSummary } from "../../api/requestsAdmin";
import { Dropdown } from "../shared/Dropdown";
import { useColumnFilters, type ColumnFilterSpec } from "../shared/columnFilters";
import { Tooltip } from "../shared/Tooltip";
import { publishAssignedRequestCount } from "../../hooks/useAssignedRequestCount";

const ApprovePermission = "request.approve";

type Mode = "reject" | "reassign" | "amend";

interface RequestsContextValue {
  modes: Record<string, Mode | undefined>;
  approvers: Approver[] | null;
  catalog: AccessGroupCatalog;
  onDecide: (request: RequestSummary, approve: boolean, notes: string | null, amendedAccessGroups?: Record<string, string[]>) => Promise<void>;
  onShowWaitingTask: (request: RequestSummary) => void;
  onReassign: (request: RequestSummary, newApproverId: string) => Promise<void>;
  onComplete: (request: RequestSummary) => Promise<void>;
  onCancel: (request: RequestSummary) => void;
}

const RequestsContext = createContext<RequestsContextValue | null>(null);

function typeLabel(type: string): string {
  switch (type) {
	case "ProviderReallocation":
	  return "Reallocate AI tool allowance";
	case "ProviderDisable":
	  return "Switch off an AI tool";
	case "ProviderEnable":
	  return "Switch on an AI tool";
	case "ManualVendorAction":
	  return "Manual vendor task";
	case "JoinGroup":
	  return "Join a team";
	default:
	  return type;
  }
}

function formatLogged(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function isPending(request: RequestSummary): boolean {
  return request.status === "Pending";
}

function requesterLabel(request: RequestSummary): string {
  const name = request.personDisplayName?.trim() ?? "";
  return name !== "" ? name : (shortEmail(request.personEmail) ?? request.personId);
}

function assignedLabel(request: RequestSummary): string | null {
  const name = request.assignedToName?.trim() ?? "";
  return name !== "" ? name : shortEmail(request.assignedToEmail ?? null);
}

/**
 * The approver's request table: who, what, when and assigned to, with Approve, Reject (reason required), Amend and Reassign per
 * row, and an expandable, formatted detail view of each request. Without `assignedToMe` it is the Administration "Requests" screen's
 * table of every request the person may act on, with its completed and withdrawn toggles and a table that fills the screen. With
 * it, it is the "Waiting for my decision" section of My Requests: only the open requests in the signed-in person's own queue (assigned to them or
 * routed to a role they hold, and, for `request.approve.any`, assigned to nobody), in a
 * compact table that sits in the page's flow and shows nothing at all when there are none; `onVisibleChange` tells the page which,
 * so it can let itself scroll while the section is there. `view` lays that section out as a table or as tiles, as the page's own
 * requests are.
 */
export function ApproverRequests({
  assignedToMe = false,
  onVisibleChange,
}: {
  assignedToMe?: boolean;
  /** Kept for callers; the "Waiting for my decision" section is one list of decisions whatever the view. */
  view?: "table" | "tiles";
  onVisibleChange?: (visible: boolean) => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; requests: RequestSummary[] } | { kind: "error"; message: string }>({ kind: "loading" });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [modes, setModes] = useState<Record<string, Mode | undefined>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  // null until the user touches the toggle: history is then shown automatically when nothing is open.
  const [showCompleted, setShowCompleted] = useState<boolean | null>(null);
  const [showWithdrawn, setShowWithdrawn] = useState(false);
  const [approvers, setApprovers] = useState<Approver[] | null>(null);
  const catalog = useAccessGroupCatalog();

  const denied = t("admin.denied.description", { permissions: ApprovePermission });

  const load = useCallback(async () => {
	const result = await fetchPendingRequests(!assignedToMe, assignedToMe);
	if (result.kind === "ok") {
	  if (assignedToMe) {
		publishAssignedRequestCount(result.response.length);
	  }
	  setState({ kind: "loaded", requests: result.response });
	} else if (result.kind === "denied") {
	  setState({ kind: "error", message: t("admin.requests.denied.description") });
	} else {
	  setState({ kind: "error", message: result.message });
	}
  }, [t, assignedToMe]);

  useEffect(() => {
	void load();
  }, [load]);

  const setExpandedFor = (id: string, open: boolean): void => {
	setExpanded((current) => {
	  const next = new Set(current);
	  if (open) {
		next.add(id);
	  } else {
		next.delete(id);
	  }

	  return next;
	});
  };


  const closePanel = (id: string): void => {
	setModes((current) => ({ ...current, [id]: undefined }));
  };

  const context: RequestsContextValue = {
	modes,
	approvers,
	catalog,
	onShowWaitingTask: (request) => {
	  // The waiting manual task is already in this list (it is open, so it shows without "completed"); filter to the person rather than duplicating it.
	  setShowCompleted(false);
	  setSearch(requesterLabel(request));
	},
	onDecide: async (request, approve, notes, amendedAccessGroups) => {
	  setBusyId(request.id);
	  const result = await decideRequest(request.id, approve, notes, amendedAccessGroups);
	  setBusyId(null);
	  if (result.kind === "ok") {
		await load();
	  } else {
		setExpandedFor(request.id, true);
		toast.show(result.kind === "denied" ? denied : result.message, "error");
	  }
	},
	onReassign: async (request, newApproverId) => {
	  setBusyId(request.id);
	  const result = await reassignRequest(request.id, newApproverId);
	  setBusyId(null);
	  if (result.kind === "ok") {
		closePanel(request.id);
		await load();
	  } else {
		toast.show(result.kind === "denied" ? denied : result.message, "error");
	  }
	},
	onComplete: async (request) => {
	  setBusyId(request.id);
	  const result = await completeManualTask(request.id, null);
	  setBusyId(null);
	  if (result.kind === "ok") {
		await load();
	  } else {
		toast.show(result.kind === "denied" ? denied : result.message, "error");
	  }
	},
	onCancel: (request) => closePanel(request.id),
  };

  const openPanel = async (request: RequestSummary, mode: Mode): Promise<void> => {
	setModes((current) => ({ ...current, [request.id]: mode }));
	setExpandedFor(request.id, true);
	if (mode === "reassign" && approvers === null) {
	  const result = await fetchApprovers();
	  if (result.kind === "ok") {
		setApprovers(result.response);
	  } else {
		toast.show(result.kind === "denied" ? denied : result.message, "error");
	  }
	}
  };

  const allRequests = useMemo(() => (state.kind === "loaded" ? state.requests : []), [state]);
  const hasOpenRequests = allRequests.some(isPending);
  const occupiesPage = assignedToMe && (state.kind === "error" || allRequests.length > 0);
  useEffect(() => {
	onVisibleChange?.(occupiesPage);
  }, [occupiesPage, onVisibleChange]);
  const showingCompleted = !assignedToMe && (showCompleted ?? !hasOpenRequests);

  const visibleRequests = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const all = (showingCompleted ? allRequests : allRequests.filter(isPending)).filter((request) => showWithdrawn || request.status !== "Withdrawn");
    if (needle === "") {
      return all;
    }
    return all.filter((request) => (request.personDisplayName ?? "").toLowerCase().includes(needle) || (request.personEmail ?? "").toLowerCase().includes(needle));
  }, [allRequests, showingCompleted, showWithdrawn, search]);
  const hasActions = visibleRequests.some(isPending);
  const unassigned = t("admin.requests.table.unassigned", "Unassigned");
  // The columns the table can be filtered on, each by the values its cells show.
  const requestFilters = useMemo((): ColumnFilterSpec<RequestSummary>[] => [
	{ id: "from", kind: "set", value: requesterLabel },
	{ id: "type", kind: "set", value: (request) => (request.requestType === "" ? "" : typeLabel(request.requestType)) },
	{ id: "assignedTo", kind: "set", value: (request) => assignedLabel(request) ?? unassigned },
	{ id: "status", kind: "set", value: (request) => request.status },
  ], [unassigned]);
  const { filtered: shownRequests, heading } = useColumnFilters(visibleRequests, requestFilters);

  /** The buttons that decide a pending request; nothing for one already decided. */
  const renderActions = (request: RequestSummary): React.JSX.Element | null =>
    !isPending(request) ? null : request.requestType === "ManualVendorAction" ? (
    <div className={styles.actions}>
      <button type="button" className="button button-primary" disabled={busyId === request.id} onClick={() => void context.onComplete(request)}>
      {t("admin.requests.card.markCompleted", "Mark completed")}
      </button>
      <button type="button" className="button button-secondary" disabled={busyId === request.id} onClick={() => void openPanel(request, "reject")}>
      {t("admin.requests.card.reject")}
      </button>
    </div>
    ) : (
    <div className={styles.actions}>
      <button type="button" className="button button-primary" disabled={busyId === request.id} onClick={() => void context.onDecide(request, true, null)}>
      {t("admin.requests.card.approve")}
      </button>
      {Object.keys(requestedGroups(request)).length > 0 && (
      <button type="button" className="button button-secondary" disabled={busyId === request.id} onClick={() => void openPanel(request, "amend")}>
        {t("admin.requests.amend.open")}
      </button>
      )}
      <button type="button" className="button button-secondary" disabled={busyId === request.id} onClick={() => void openPanel(request, "reject")}>
      {t("admin.requests.card.reject")}
      </button>
      <button type="button" className="button button-secondary" disabled={busyId === request.id} onClick={() => void openPanel(request, "reassign")}>
      {t("admin.requests.card.reassign")}
      </button>
    </div>
    );

  /** The two buttons a quick decision needs; amending and reassigning are in the detail beneath. */
  const renderQuickActions = (request: RequestSummary): React.JSX.Element => (
    <>
      <button
        type="button"
        className="button button-primary"
        disabled={busyId === request.id}
        onClick={() => void (request.requestType === "ManualVendorAction" ? context.onComplete(request) : context.onDecide(request, true, null))}
      >
        {request.requestType === "ManualVendorAction" ? t("myRequests.decision.done", "Done") : t("admin.requests.card.approve")}
      </button>
      <button type="button" className="button button-secondary" disabled={busyId === request.id} onClick={() => void openPanel(request, "reject")}>
        {t("admin.requests.card.reject")}
      </button>
    </>
  );

  /** The less common actions, offered in the detail of a request that can be amended or handed to someone else. */
  const renderMoreActions = (request: RequestSummary): React.JSX.Element | null =>
    request.requestType === "ManualVendorAction" ? null : (
      <div className={styles.actions}>
        {Object.keys(requestedGroups(request)).length > 0 && (
          <button type="button" className="button button-secondary" disabled={busyId === request.id} onClick={() => void openPanel(request, "amend")}>
            {t("admin.requests.amend.open")}
          </button>
        )}
        <button type="button" className="button button-secondary" disabled={busyId === request.id} onClick={() => void openPanel(request, "reassign")}>
          {t("admin.requests.card.reassign")}
        </button>
      </div>
    );

  const columns = useMemo((): TableColumn<RequestSummary>[] => {
  const allColumns: TableColumn<RequestSummary>[] = [
	{
	  id: "from",
	  name: heading("from", t("admin.requests.table.from", "From")),
	  selector: (request) => requesterLabel(request),
	  cell: (request) => {
		const hasName = (request.personDisplayName?.trim() ?? "") !== "";
		return <Tooltip label={hasName ? (request.personEmail ?? undefined) : undefined}>{requesterLabel(request)}</Tooltip>;
	  },
	  sortable: true,
	  wrap: true,
	  grow: 2,
	},
	{
	  id: "type",
	  name: heading("type", t("admin.requests.table.type", "Type")),
	  selector: (request) => typeLabel(request.requestType),
	  sortable: true,
	  wrap: true,
	  grow: 2,
	},
	{
	  id: "logged",
	  name: t("admin.requests.table.logged", "Logged"),
	  selector: (request) => formatLogged(request.createdAt),
	  sortFunction: (a, b) => a.createdAt.localeCompare(b.createdAt),
	  sortable: true,
	  wrap: true,
	  grow: 2,
	},
	{
	  id: "assignedTo",
	  name: heading("assignedTo", t("admin.requests.table.assignedTo", "Assigned to")),
	  selector: (request) => assignedLabel(request) ?? unassigned,
	  sortable: true,
	  wrap: true,
	  grow: 2,
	},
	{
	  id: "status",
	  name: heading("status", t("admin.requests.table.status", "Status")),
	  selector: (request) => request.status,
	  sortable: true,
	  wrap: true,
	},
	{
	  id: "decided",
	  name: t("admin.requests.table.decided", "Decided"),
	  selector: (request) => (request.decidedAt ? formatLogged(request.decidedAt) : ""),
	  sortFunction: (a, b) => (a.decidedAt ?? "").localeCompare(b.decidedAt ?? ""),
	  sortable: true,
	  wrap: true,
	  grow: 2,
	},
	{
	  id: "decidedBy",
	  name: t("admin.requests.table.decidedBy", "Decided by"),
	  selector: (request) => request.decidedByName ?? request.decidedByEmail ?? "",
	  sortable: true,
	  wrap: true,
	  grow: 1,
	},
	{
	  id: "actions",
	  name: <span className="visually-hidden">{t("dataTable.actionsColumn")}</span>,
	  button: true,
	  ignoreRowClick: true,
	  grow: 4,
	  minWidth: "24rem",
	  cell: renderActions,
	},
  ];
  const mineColumns = ["from", "type", "logged", "actions"];
  const shown = assignedToMe ? allColumns.filter((column) => mineColumns.includes(String(column.id))) : allColumns;
  return hasActions ? shown : shown.filter((column) => column.id !== "actions");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, busyId, approvers, modes, hasActions, heading, assignedToMe]);

  // The section on My Requests is only there while something is assigned to the person; it does not announce an empty queue.
  if (assignedToMe && (state.kind === "loading" || (state.kind === "loaded" && allRequests.length === 0))) {
	return null;
  }

  if (state.kind === "loading") {
	return <LoadingState label={t("admin.requests.loading")} />;
  }

  if (state.kind === "error") {
	return <ErrorState title={t("admin.requests.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  return (
	<RequestsContext.Provider value={context}>
      <section className={assignedToMe ? undefined : `${adminStyles.section} ${adminStyles.usersSection}`} aria-labelledby={assignedToMe ? "assigned-requests-heading" : undefined}>
        {assignedToMe && (
          <h2 id="assigned-requests-heading" className="panel-title">
            {t("myRequests.assignedTitle", "Waiting for my decision")}
          </h2>
        )}
        {!assignedToMe && <div className={adminStyles.tableToolbar}>
          <label className={adminStyles.toolbarToggle}>
            <input type="checkbox" className={adminStyles.allocationToggle} checked={showingCompleted} onChange={(event) => setShowCompleted(event.target.checked)} />
            {t("admin.requests.showCompleted", "Show Completed Requests")}
          </label>
          <label className={adminStyles.toolbarToggle}>
            <input type="checkbox" className={adminStyles.allocationToggle} checked={showWithdrawn} onChange={(event) => setShowWithdrawn(event.target.checked)} />
            {t("admin.requests.showWithdrawn", "Show Withdrawn Requests")}
          </label>
          <input type="search" className={adminStyles.peopleSearch} value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("admin.requests.search", "Search by name or email")} aria-label={t("admin.requests.search", "Search by name or email")} />
        </div>}
      <div className={assignedToMe ? adminStyles.tableWrapper : `${adminStyles.tableWrapper} ${adminStyles.peopleTableWrapper}`}>
		{assignedToMe ? (
		  <ul className={styles.decisions} aria-label={t("myRequests.assignedTitle", "Waiting for my decision")}>
			{shownRequests.map((request) => (
			  <DecisionRow
				key={request.id}
				headline={decisionHeadline(request, t)}
				actions={renderQuickActions(request)}
				expanded={expanded.has(request.id)}
				onToggle={() => setExpandedFor(request.id, !expanded.has(request.id))}
			  >
				<ExpandedRequest data={request} />
				{renderMoreActions(request)}
			  </DecisionRow>
			))}
		  </ul>
		) : (
		<DataTable
		  persistTableHead
		  columns={columns}
		  data={shownRequests}
		  className={assignedToMe ? undefined : adminStyles.peopleDataTable}
		  keyField="id"
		  ariaLabel={t("admin.requests.title", "Requests")}
		  theme={adminTableTheme}
		  striped
		  highlightOnHover
		  columnSeparator="subtle"
		  pagination
		  paginationComponent={TablePager}
		  noDataComponent={
				showingCompleted || hasOpenRequests
				  ? <EmptyState title={t("admin.requests.emptyTitle")} description={t("admin.requests.emptyDescription")} />
				  : <EmptyState title={t("admin.requests.emptyOpenTitle", "No open requests")} description={t("admin.requests.emptyOpenDescription", "Nothing is waiting for a decision. Turn on Show Completed Requests to see past requests.")} />
			  }
		  fixedHeader={!assignedToMe}
		  fixedHeaderScrollHeight={assignedToMe ? undefined : "100%"}
		  paginationPerPage={assignedToMe ? 10 : 25}
		  paginationRowsPerPageOptions={[10, 25, 50, 100]}
		  defaultSortFieldId="logged"
		  expandableRows
		  expandableRowExpanded={(request) => expanded.has(request.id)}
		  onRowExpandToggled={(open, request) => setExpandedFor(request.id, open)}
		  expandableRowsComponent={ExpandedRequest}
		/>
		)}
	  </div>
      </section>
	</RequestsContext.Provider>
  );
}

function ExpandedRequest({ data: request }: { data: RequestSummary }): React.JSX.Element {
  const { t } = useTranslation();
  const context = useContext(RequestsContext);
  const [reason, setReason] = useState("");
  const [newApproverId, setNewApproverId] = useState("");

  if (context === null) {
	return <RequestDetail request={request} catalog={{}} />;
  }

  const mode = context.modes[request.id];

  return (
	<div className={styles.expanded}>
	  <RequestDetail request={request} catalog={context.catalog} />
	  {request.status === "AwaitingDependencies" && (
		<p className={styles.waiting}>
		  {t("admin.requests.waiting.text")}{" "}
		  <button type="button" className="button button-secondary" onClick={() => context.onShowWaitingTask(request)}>
			{t("admin.requests.waiting.show")}
		  </button>
		</p>
	  )}
	  {mode === "amend" && <AmendPanel request={request} catalog={context.catalog} />}
	  {mode === "reject" && (
		<div className={styles.panel}>
		  <div className={`${adminStyles.field} ${styles.grow}`}>
			<label htmlFor={`reason-${request.id}`}>{t("admin.requests.reject.reason", "Reason for rejecting (required)")}</label>
			<textarea id={`reason-${request.id}`} rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
		  </div>
		  <button type="button" className="button button-primary" disabled={reason.trim() === ""} onClick={() => void context.onDecide(request, false, reason.trim())}>
			{t("admin.requests.reject.confirm", "Confirm rejection")}
		  </button>
		  <button type="button" className="button button-secondary" onClick={() => context.onCancel(request)}>
			{t("admin.requests.cancel", "Cancel")}
		  </button>
		</div>
	  )}
	  {mode === "reassign" && (
		<div className={styles.panel}>
		  <div className={`${adminStyles.field} ${styles.grow}`}>
			<label htmlFor={`approver-${request.id}`}>{t("admin.requests.card.newApprover")}</label>
			<Dropdown
			  id={`approver-${request.id}`}
			  options={[
			    { value: "", label: t("admin.requests.reassign.choose", "Choose an approver…") },
			    ...(context.approvers ?? []).map((approver) => ({ value: approver.personId, label: `${approver.displayName} (${approver.email})` })),
			  ]}
			  value={newApproverId}
			  onChange={setNewApproverId}
			/>
		  </div>
		  <button type="button" className="button button-primary" disabled={newApproverId === ""} onClick={() => void context.onReassign(request, newApproverId)}>
			{t("admin.requests.card.reassign")}
		  </button>
		  <button type="button" className="button button-secondary" onClick={() => context.onCancel(request)}>
			{t("admin.requests.cancel", "Cancel")}
		  </button>
		</div>
	  )}
	</div>
  );
}

interface PayloadProvider {
  vendorId: string;
  enabled: boolean;
  amountUsd: number;
  accessGroups: string[];
}

function parsePayload(json: string): Record<string, unknown> | null {
  try {
    const value: unknown = JSON.parse(json);
    return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function pick<T>(payload: Record<string, unknown>, camel: string): T | undefined {
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
  return (payload[camel] ?? payload[pascal]) as T | undefined;
}

function readProviders(payload: Record<string, unknown>): PayloadProvider[] {
  return (pick<Record<string, unknown>[]>(payload, "providers") ?? []).map(
    (item): PayloadProvider => ({
      vendorId: String(pick<string>(item, "vendorId") ?? ""),
      enabled: pick<boolean>(item, "enabled") === true,
      amountUsd: Number(pick<number>(item, "amountUsd") ?? 0),
      accessGroups: (pick<string[] | null>(item, "accessGroups") ?? []).map(String),
    }),
  );
}

/** The access groups a provider-allocation request asks for, by vendor, for the providers it switches on. */
function requestedGroups(request: RequestSummary): Record<string, string[]> {
  const payload = isProviderAllocationRequest(request.requestType) ? parsePayload(request.payloadJson) : null;
  if (payload === null) {
    return {};
  }

  return Object.fromEntries(readProviders(payload).filter((provider) => provider.enabled && provider.accessGroups.length > 0).map((provider) => [provider.vendorId, provider.accessGroups]));
}

/** The approver's amendment of the requested access groups: pre-filled with what was asked for, approving with the amended sets. */
function AmendPanel({ request, catalog }: { request: RequestSummary; catalog: AccessGroupCatalog }): React.JSX.Element {
  const { t } = useTranslation();
  const context = useContext(RequestsContext);
  const [amended, setAmended] = useState<Record<string, string[]>>(() => requestedGroups(request));
  const requested = requestedGroups(request);
  const vendorIds = Object.keys(amended);
  const incomplete = vendorIds.some((vendorId) => amended[vendorId].length === 0);

  const toggle = (vendorId: string, groupId: string, checked: boolean): void => {
    setAmended((current) => ({
      ...current,
      [vendorId]: checked ? [...current[vendorId], groupId] : current[vendorId].filter((id) => id !== groupId),
    }));
  };

  return (
    <div className={styles.panel}>
      {vendorIds.map((vendorId) => {
        const offered = catalog[vendorId];
        // A requested group the catalogue no longer offers is still selected, so it is shown (and can be unticked) rather than
        // being counted invisibly. It stays listed after unticking, so the approver can change their mind.
        const unknown = offered ? (requested[vendorId] ?? []).filter((id) => !offered.some((group) => group.id === id)) : [];
        const errorId = `amend-${request.id}-${vendorId}-groups-error`;
        const missing = amended[vendorId].length === 0;
        return (
          <fieldset key={vendorId} className={adminStyles.permissionFieldset} aria-describedby={missing ? errorId : undefined}>
            <legend>{t("admin.requests.amend.legend", { provider: request.providerNames?.[vendorId] ?? vendorId })}</legend>
            <div className={adminStyles.permissionGrid}>
              {(offered ?? []).map((group) => (
                <label key={group.id} className={adminStyles.permissionOption}>
                  <input type="checkbox" checked={amended[vendorId].includes(group.id)} onChange={(event) => toggle(vendorId, group.id, event.target.checked)} />
                  <span>{group.displayName}</span>
                </label>
              ))}
              {unknown.map((id) => (
                <label key={id} className={adminStyles.permissionOption}>
                  <input type="checkbox" checked={amended[vendorId].includes(id)} onChange={(event) => toggle(vendorId, id, event.target.checked)} />
                  <span>{t("admin.requests.amend.unknownGroup", { id })}</span>
                </label>
              ))}
            </div>
            {missing && (
              <p id={errorId} className={adminStyles.feedback}>
                {t("accessGroups.required")}
              </p>
            )}
          </fieldset>
        );
      })}
      <button type="button" className="button button-primary" disabled={incomplete} onClick={() => void context?.onDecide(request, true, null, amended)}>
        {t("admin.requests.amend.confirm")}
      </button>
      <button type="button" className="button button-secondary" onClick={() => context?.onCancel(request)}>
        {t("admin.requests.cancel", "Cancel")}
      </button>
    </div>
  );
}

/** The access groups each provider had when the request was raised, for the providers whose groups were recorded. */
function currentGroups(request: RequestSummary): Record<string, string[]> {
  return Object.fromEntries(
    (request.providerChanges ?? []).flatMap((change) => (change.beforeAccessGroups ? [[change.vendorId, change.beforeAccessGroups]] : [])),
  );
}

/**
 * The request's details, as the shared request history shows them, followed for a provider-allocation request that asks for vendor
 * access groups (AIA-109) by the groups it asks for, per provider, so the approver sees them before approving or amending. When the
 * request recorded the groups the person already had, they are shown beside the requested ones: a request that keeps a provider on
 * and only changes its groups shows the same totals before and after, so this is the only place the change is visible. A request
 * that recorded no groups (raised before they were recorded) shows only the requested ones.
 */
function RequestDetail({ request, catalog }: { request: RequestSummary; catalog: AccessGroupCatalog }): React.JSX.Element {
  const { t } = useTranslation();
  const groups = Object.entries(requestedGroups(request));
  const current = currentGroups(request);
  const showCurrent = groups.some(([vendorId]) => vendorId in current);
  const names = (vendorId: string, ids: string[]): string =>
    ids.map((id) => (catalog[vendorId] ?? []).find((group) => group.id === id)?.displayName ?? id).join(", ");

  return (
    <>
      <RequestDetailContent requestType={request.requestType} payloadJson={request.payloadJson} providerChanges={request.providerChanges ?? null} />
      {groups.length > 0 && (
        <table className={styles.detailTable}>
          <thead>
            <tr>
              <th scope="col">{t("admin.requests.detail.provider", "Provider")}</th>
              {showCurrent && <th scope="col">{t("admin.requests.detail.currentGroups")}</th>}
              <th scope="col">{t("admin.requests.detail.groups")}</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(([vendorId, ids]) => (
              <tr key={vendorId}>
                <td>{request.providerNames?.[vendorId] ?? vendorId}</td>
                {showCurrent && <td>{current[vendorId]?.length ? names(vendorId, current[vendorId]) : t("admin.requests.detail.noGroups")}</td>}
                <td>{names(vendorId, ids)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
